import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { gearAPI } from '../utils/api';
import { Plus, Edit, Trash2 } from 'lucide-react';

const Gear = () => {
  const [gear, setGear] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedGear, setSelectedGear] = useState(null);
  const [formData, setFormData] = useState({ name: '', category: '', description: '' });

  useEffect(() => {
    fetchGear();
  }, []);

  const fetchGear = async () => {
    try {
      const response = await gearAPI.getAll();
      setGear(response.data);
    } catch (error) {
      console.error('Failed to fetch gear:', error);
    }
  };

  const handleCreate = () => {
    setSelectedGear(null);
    setFormData({ name: '', category: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedGear(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (gearId) => {
    if (window.confirm('Are you sure you want to delete this gear?')) {
      try {
        await gearAPI.delete(gearId);
        fetchGear();
      } catch (error) {
        alert('Failed to delete gear');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedGear) {
        await gearAPI.update(selectedGear.gear_id, formData);
      } else {
        await gearAPI.create(formData);
      }
      setShowModal(false);
      fetchGear();
    } catch (error) {
      alert('Failed to save gear');
    }
  };

  const groupedGear = gear.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="gear-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-serif mb-2">Gear Management</h1>
            <p className="text-muted-foreground">Manage equipment and gear inventory</p>
          </div>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90"
            data-testid="create-gear-button"
          >
            <Plus size={16} />
            ADD GEAR
          </button>
        </div>

        {Object.keys(groupedGear).map(category => (
          <div key={category} className="mb-8">
            <h2 className="text-2xl font-serif mb-4">{category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groupedGear[category].map(item => (
                <div key={item.gear_id} className="card p-4" data-testid={`gear-${item.gear_id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-medium">{item.name}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(item)}
                        className="p-1 border border-border hover:border-gold transition-colors"
                        data-testid={`edit-gear-${item.gear_id}`}
                      >
                        <Edit size={12} />
                      </button>
                      <button
                        onClick={() => handleDelete(item.gear_id)}
                        className="p-1 border border-destructive text-destructive hover:bg-destructive/10 transition-colors"
                        data-testid={`delete-gear-${item.gear_id}`}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                  {item.description && <p className="text-sm text-muted-foreground">{item.description}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}

        {gear.length === 0 && (
          <div className="card p-12 text-center">
            <p className="text-muted-foreground">No gear added yet. Start building your inventory!</p>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="gear-modal">
          <div className="bg-background w-full max-w-md p-8">
            <h2 className="text-2xl font-serif mb-6">{selectedGear ? 'Edit Gear' : 'Add Gear'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-2 uppercase tracking-wide">Gear Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  required
                  data-testid="gear-name"
                />
              </div>

              <div>
                <label className="block text-sm mb-2 uppercase tracking-wide">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border-b border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  required
                  placeholder="e.g., Camera Bodies, Lenses, Lighting"
                  data-testid="gear-category"
                />
              </div>

              <div>
                <label className="block text-sm mb-2 uppercase tracking-wide">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-input bg-transparent text-foreground placeholder:text-muted-foreground"
                  rows="3"
                  data-testid="gear-description"
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  className="bg-primary text-primary-foreground px-6 py-3 hover:bg-primary/90"
                  data-testid="gear-submit"
                >
                  {selectedGear ? 'UPDATE' : 'ADD'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="border border-border px-6 py-3 hover:bg-accent"
                  data-testid="gear-cancel"
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Gear;